import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Loader2 } from 'lucide-react';

interface JobDetailsDialogProps {
  isOpen: boolean;
  onClose: () => void;
  jobName: string;
  defaultMinutes: number;
  onConfirm: (minutes: number, notes: string) => void;
  isLoading?: boolean;
}

export const JobDetailsDialog = ({
  isOpen,
  onClose,
  jobName,
  defaultMinutes,
  onConfirm,
  isLoading = false,
}: JobDetailsDialogProps) => {
  const [hours, setHours] = useState((defaultMinutes / 60).toFixed(2));
  const [notes, setNotes] = useState('');

  const handleConfirm = () => {
    const hoursNum = parseFloat(hours);
    if (!isNaN(hoursNum) && hoursNum > 0) {
      const minutesNum = Math.round(hoursNum * 60);
      onConfirm(minutesNum, notes);
      setHours((defaultMinutes / 60).toFixed(2));
      setNotes('');
      // Parent will close the dialog after processing
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Job Details</DialogTitle>
          <DialogDescription>
            Set the time and optional notes for "{jobName}"
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="hours">Time (hours)</Label>
            <Input
              id="hours"
              type="number"
              step="0.1"
              min="0.1"
              value={hours}
              onChange={(e) => setHours(e.target.value)}
              placeholder="Enter hours (e.g., 1.5)"
              disabled={isLoading}
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="notes">Notes (optional)</Label>
            <Textarea
              id="notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Add any specific notes for this job..."
              rows={3}
              disabled={isLoading}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button onClick={handleConfirm} disabled={isLoading}>
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Adding job...
              </>
            ) : (
              'Confirm'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
